import type { IconName } from '../shared/components/Icon';

/*
 * What the workspace knows about a document before it has rendered anything.
 *
 * One address per row: the tab label, the icon on the tab, and — for the addresses
 * that carry a record — the part of the address that tells two open copies apart.
 * The catalogue is the single list the tab strip, the start page, and the command
 * palette all read, so a new console page is described in exactly one place.
 */

export interface PanelDescriptor {
  /** A console address, with `:name` standing for one segment of anything. */
  readonly pattern: string;
  readonly titleKey: string;
  readonly icon: IconName;
  /** Path parameter appended to the tab label, so two open records never read alike. */
  readonly detailParam?: string;
  /** Query parameter appended to the tab label, for pages a query re-aims. */
  readonly detailQuery?: string;
}

export const consoleRoot = '/console';

export const panelCatalog: readonly PanelDescriptor[] = [
  { pattern: '/console', titleKey: 'nav.dashboard', icon: 'dashboard' },
  { pattern: '/console/experiences', titleKey: 'app.workspace', icon: 'sparkle' },
  { pattern: '/console/journals', titleKey: 'nav.journals', icon: 'journal' },
  { pattern: '/console/journals/new', titleKey: 'journals.newJournal', icon: 'plus' },
  { pattern: '/console/journals/:journalId/edit', titleKey: 'journals.editJournal', icon: 'journal', detailParam: 'journalId' },
  { pattern: '/console/journals/:journalId', titleKey: 'workspace.documents.journal', icon: 'journal', detailParam: 'journalId' },
  { pattern: '/console/ledger', titleKey: 'nav.ledger', icon: 'reports' },
  { pattern: '/console/accounts', titleKey: 'nav.accounts', icon: 'accounts' },
  { pattern: '/console/persons', titleKey: 'nav.persons', icon: 'user' },
  { pattern: '/console/customers', titleKey: 'nav.customers', icon: 'user' },
  { pattern: '/console/suppliers', titleKey: 'nav.suppliers', icon: 'user' },
  { pattern: '/console/identity', titleKey: 'nav.interOrganizationIdentity', icon: 'sparkle' },
  { pattern: '/console/currency/rates', titleKey: 'nav.exchangeRates', icon: 'exchange' },
  { pattern: '/console/currency/administration', titleKey: 'nav.currencyAdministration', icon: 'exchange' },
  { pattern: '/console/inventory', titleKey: 'nav.inventory', icon: 'inventory' },
  { pattern: '/console/reports', titleKey: 'nav.reports', icon: 'reports', detailQuery: 'report' },
  { pattern: '/console/receivables', titleKey: 'nav.receivables', icon: 'receivables' },
  { pattern: '/console/payables', titleKey: 'nav.payables', icon: 'payables' },
  { pattern: '/console/treasury', titleKey: 'nav.treasury', icon: 'cash' },
  { pattern: '/console/sales', titleKey: 'nav.sales', icon: 'sales' },
  { pattern: '/console/purchasing', titleKey: 'nav.purchasing', icon: 'purchase' },
  { pattern: '/console/automation', titleKey: 'nav.automation', icon: 'exchange' },
  { pattern: '/console/payroll', titleKey: 'nav.payroll', icon: 'payroll' },
  { pattern: '/console/tax', titleKey: 'nav.tax', icon: 'reports' },
  { pattern: '/console/fixed-assets', titleKey: 'nav.fixedAssets', icon: 'inventory' },
  { pattern: '/console/budgeting', titleKey: 'nav.budgeting', icon: 'trend' },
  { pattern: '/console/costing', titleKey: 'nav.costing', icon: 'trend', detailQuery: 'tab' },
  { pattern: '/console/construction', titleKey: 'constructionWorkspace.nav', icon: 'trend', detailQuery: 'tab' },
  { pattern: '/console/workforce', titleKey: 'nav.workforce', icon: 'user', detailQuery: 'profile' },
  { pattern: '/console/projects', titleKey: 'nav.projects', icon: 'trend' },
  { pattern: '/console/projects/:projectId', titleKey: 'nav.projects', icon: 'trend', detailParam: 'projectId' },
  { pattern: '/console/personal-finance', titleKey: 'nav.personalFinance', icon: 'cash' },
  { pattern: '/console/personal-finance/loans/:loanId', titleKey: 'nav.personalFinance', icon: 'cash', detailParam: 'loanId' },
  { pattern: '/console/personal-finance/members/:memberId', titleKey: 'nav.personalFinance', icon: 'user', detailParam: 'memberId' },
  { pattern: '/console/personal-finance/expenses/:expenseId', titleKey: 'nav.personalFinance', icon: 'reports', detailParam: 'expenseId' },
  { pattern: '/console/settings', titleKey: 'nav.settings', icon: 'settings' },
  { pattern: '/console/settings/access', titleKey: 'nav.access', icon: 'user' },
  { pattern: '/console/settings/audit', titleKey: 'auditLog.title', icon: 'reports' },
];

export interface PanelMatch {
  readonly descriptor: PanelDescriptor;
  readonly params: Readonly<Record<string, string>>;
}

function segmentsOf(pathname: string): string[] {
  return pathname.split('/').filter(Boolean);
}

/** Splits an address into the part that routes and the part that qualifies it. */
export function splitAddress(address: string): { pathname: string; search: string } {
  const marker = address.indexOf('?');
  if (marker < 0) return { pathname: address, search: '' };
  return { pathname: address.slice(0, marker), search: address.slice(marker) };
}

export function matchPanel(address: string): PanelMatch | undefined {
  const { pathname } = splitAddress(address);
  const parts = segmentsOf(pathname);
  for (const descriptor of panelCatalog) {
    const patternParts = segmentsOf(descriptor.pattern);
    if (patternParts.length !== parts.length) continue;
    const params: Record<string, string> = {};
    const matched = patternParts.every((patternPart, index) => {
      const part = parts[index];
      if (part === undefined) return false;
      if (patternPart.startsWith(':')) {
        params[patternPart.slice(1)] = decodeURIComponent(part);
        return true;
      }
      return patternPart === part;
    });
    if (matched) return { descriptor, params };
  }
  return undefined;
}

/** Identifiers are long and meaningless on a tab; the tail of one is enough to tell two apart. */
function shortenDetail(value: string): string {
  const cleaned = value.replaceAll('-', '');
  return cleaned.length > 8 ? cleaned.slice(-6).toLocaleUpperCase() : value;
}

/** Only the lookup half of `useTranslation`, so this file needs nothing from i18next's types. */
export type Translate = (key: string) => string;

export interface PanelIdentity {
  readonly title: string;
  readonly icon: IconName;
  readonly detail: string | null;
}

/** The label and icon a tab wears for an address, resolved in the active language. */
export function describeAddress(address: string, t: Translate): PanelIdentity {
  const match = matchPanel(address);
  if (match === undefined) return { title: t('errors.notFoundTitle'), icon: 'alert', detail: null };

  const { descriptor, params } = match;
  const title = t(descriptor.titleKey);
  if (descriptor.detailParam !== undefined) {
    const value = params[descriptor.detailParam];
    if (value !== undefined && value.length > 0) return { title, icon: descriptor.icon, detail: shortenDetail(value) };
  }
  if (descriptor.detailQuery !== undefined) {
    const { search } = splitAddress(address);
    const value = new URLSearchParams(search).get(descriptor.detailQuery);
    if (value !== null && value.length > 0) return { title, icon: descriptor.icon, detail: value };
  }
  return { title, icon: descriptor.icon, detail: null };
}

/** True for an address the console can actually draw, so a stored layout cannot resurrect a dead tab. */
export function isKnownAddress(address: string): boolean {
  return matchPanel(address) !== undefined;
}

/** Trailing slashes and an empty query would otherwise open the same page twice. */
export function canonicalAddress(address: string): string {
  const { pathname, search } = splitAddress(address);
  const trimmed = pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  return search === '?' ? trimmed : `${trimmed}${search}`;
}
